<script setup>
// How we rate difficulty: each rating the organization's trails use,
// in its own symbol and color, with a count and a couple to try.
import { computed, inject } from 'vue';
import DifficultyIcon from '@/Components/DifficultyIcon.vue';
import './trailsPage.css';
import { DIFFICULTY_DESCRIPTIONS, blockStyle, described, difficultyLevelsIn } from '../trailsPage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const levels = computed(() => difficultyLevelsIn(props.site_data, props.org, 2));
const accent = computed(() => props.config.style?.accent || props.accent || 'var(--th-accent, #2f6b47)');
const editing = inject('tiqEditorCanvas', false);
</script>

<template>
    <section v-if="levels.length || editing" class="tp tdiff" :id="config.anchor_id || null" :style="[blockStyle(config), { '--tp-accent': accent }]">
        <div class="tp-wrap">
            <header v-if="config.title || config.subtitle" class="tp-head">
                <h2 v-if="config.title" class="tp-title">{{ config.title }}</h2>
                <p v-if="config.subtitle" class="tp-sub">{{ config.subtitle }}</p>
            </header>
            <p v-if="!(levels.length)" class="tp-empty">Nothing to show yet. This block lists the difficulty ratings your public trails use.</p>
            <div class="tdiff-grid">
                <div v-for="lv in levels" :key="lv.key" class="tdiff-item">
                    <div class="tdiff-top">
                        <DifficultyIcon
                            v-if="lv.icon"
                            class="tdiff-icon"
                            :difficulty="lv.icon.difficulty"
                            :icon-set="lv.icon.set"
                            :color="lv.icon.color"
                        />
                        <h3 class="tdiff-name">{{ lv.label }}</h3>
                        <span class="tp-mono tdiff-count">{{ lv.count }} {{ lv.count === 1 ? 'trail' : 'trails' }}</span>
                    </div>
                    <p class="tdiff-desc">{{ described(config, lv.key, DIFFICULTY_DESCRIPTIONS[lv.key] || '') }}</p>
                    <p v-if="config.show_examples !== false && lv.examples.length" class="tdiff-try">
                        Try:
                        <template v-for="(ex, i) in lv.examples" :key="ex.name">
                            <a v-if="ex.href" :href="ex.href">{{ ex.name }}</a><strong v-else>{{ ex.name }}</strong><template v-if="i < lv.examples.length - 1">, </template>
                        </template>
                    </p>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.tdiff-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 24px 32px;
    border-top: 1px solid var(--tp-line); border-bottom: 1px solid var(--tp-line); padding: 22px 0;
}
.tdiff-top { display: flex; align-items: center; gap: 12px; }
.tdiff-icon { width: 24px; height: 24px; flex: none; }
.tdiff-name {
    font-family: var(--tp-display); font-weight: 800; font-size: 24px; margin: 0; flex: 1;
    color: var(--text-heading, inherit);
}
.tdiff-count { color: var(--tp-faint); }
.tdiff-desc { color: var(--tp-muted); line-height: 1.55; margin: 10px 0 0; }
.tdiff-try { margin: 12px 0 0; font-size: 15px; color: var(--tp-muted); }
.tdiff-try a, .tdiff-try strong { color: var(--tp-ink); font-weight: 700; }
</style>
